const ROSTER_LABEL_OVERRIDES: Record<string, string> = {
  ADM: 'Administration',
  AIN: 'AIN',
  CLN: 'Cleaning',
  COOK: 'Cook',
  COOK_CHEF: 'Cook/Chef',
  CTR: 'Catering',
  DIT: 'Diversional therapy',
  EN: 'EN',
  HCPCA: 'Home care PCA',
  HCRN: 'Home care RN',
  LAO: 'Lifestyle',
  LND: 'Laundry',
  LRN: 'Lead RN',
  LSEN: 'Lifestyle EN',
  MNT: 'Maintenance',
  PCA: 'PCA',
  PCW: 'Personal care',
  QEO: 'Quality and education',
  RN: 'RN',
  RN_AGENCY: 'Agency RN',
  RNAGY: 'Agency RN',
  WPLC: 'Work placement',
};

function labelKey(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function formatEnumLabel(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const override = ROSTER_LABEL_OVERRIDES[labelKey(trimmed)];
  if (override) return override;
  return trimmed
    .replace(/[_-]/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

const GENERIC_SHIFT_PURPOSES = new Set(['OTHER']);

type ShiftLabelInput = {
  serviceCode?: string | null;
  serviceType?: string | null;
  shiftPurpose?: string | null;
};

function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function normalized(value: string | null | undefined): string | null {
  return clean(value)?.toUpperCase() ?? null;
}

function label(value: string | null | undefined): string | null {
  return formatEnumLabel(value);
}

export function isGenericShiftPurpose(shiftPurpose: string | null | undefined): boolean {
  const purpose = normalized(shiftPurpose);
  return Boolean(purpose && GENERIC_SHIFT_PURPOSES.has(purpose));
}

export function isNonClientRosterPurpose(input: ShiftLabelInput): boolean {
  const purpose = normalized(input.shiftPurpose);
  if (!purpose || purpose === 'CLIENT_SERVICE') return false;
  if (
    GENERIC_SHIFT_PURPOSES.has(purpose) &&
    (clean(input.serviceCode) || clean(input.serviceType))
  ) {
    return false;
  }
  return true;
}

export function rosterShiftDisplayLabel(input: ShiftLabelInput, fallback = 'Shift'): string {
  return (
    label(input.serviceCode) ??
    label(input.serviceType) ??
    (isGenericShiftPurpose(input.shiftPurpose) ? null : label(input.shiftPurpose)) ??
    fallback
  );
}

export function rosterShiftPurposeBadge(input: ShiftLabelInput): string | null {
  if (!isNonClientRosterPurpose(input)) return null;
  return label(input.shiftPurpose);
}

export type RosterShiftContextLabelInput = ShiftLabelInput & {
  wingId?: string | null;
  wingName?: string | null;
  facilityName?: string | null;
  clientId?: string | null;
  /**
   * REQUIRED, and deliberately not optional.
   *
   * When a caller passed `clientId` but forgot this, the label silently became
   * "Unknown client" — which is what the day board showed for every home-care
   * shift after `WorkerStackedGrid` plumbed the id without the name. Nothing
   * failed; the board just quietly stopped naming clients.
   *
   * Making it required turns that omission into a compile error. Callers that
   * genuinely have no name to show pass `null` explicitly, which reads as a
   * decision rather than an oversight.
   */
  clientDisplayName: string | null;
  requiredClassification?: string | null;
  expectedTravelMinutes?: number | null;
  /** Residential facility-scoped shift without wing_id in the read model. */
  facilityCoverage?: boolean;
};

/**
 * Primary context line for a shift card (ADR-009 data-shape labelling).
 * Wing/facility coverage takes precedence over client for RAC shifts.
 */
export function rosterShiftContextLabel(input: RosterShiftContextLabelInput): string {
  const wingName = clean(input.wingName);
  const hasWing = Boolean(input.wingId || wingName);
  const classification = label(input.requiredClassification);
  const serviceLabel = rosterShiftDisplayLabel(input, 'Shift');

  if (hasWing) {
    const parts: string[] = [];
    if (clean(input.facilityName)) parts.push(clean(input.facilityName)!);
    parts.push(wingName ?? 'Wing coverage');
    if (classification) {
      parts.push(classification);
    } else if (serviceLabel !== 'Shift') {
      parts.push(serviceLabel);
    }
    return parts.join(' · ');
  }

  if (input.facilityCoverage && clean(input.facilityName) && !input.clientId) {
    const parts = [clean(input.facilityName)!, 'Wing coverage'];
    if (classification) parts.push(classification);
    else if (serviceLabel !== 'Shift') parts.push(serviceLabel);
    return parts.join(' · ');
  }

  if (input.clientId) {
    const clientPart = clean(input.clientDisplayName) ?? 'Unknown client';
    if (clientPart) {
      if (serviceLabel !== 'Shift' && !isNonClientRosterPurpose(input)) {
        return `${clientPart} · ${serviceLabel}`;
      }
      return clientPart;
    }
  }

  if (isNonClientRosterPurpose(input)) {
    return rosterShiftDisplayLabel(input, 'Non-client shift');
  }

  if (input.expectedTravelMinutes != null && input.expectedTravelMinutes > 0) {
    return rosterShiftDisplayLabel(input, 'Visit');
  }

  return serviceLabel;
}
